import { TRIP_FEEDBACK_NOTIFICATION_HELP } from './tripFeedbackNotificationHelp';
import { EVENT_REQUEST_NOTIFICATION_HELP } from './eventRequestNotificationHelp';
import { NOTE_MENTION_NOTIFICATION_HELP } from './noteMentionNotificationHelp';

const LIVE_EXPORT_NOTIFICATION_HELP_CONTENT = {
    'client.invoice.confirmed': {
        definition: 'Tells the client that an invoice is ready.',
        who: 'The invoice account owner.',
        when: 'An invoice is created or changes to sent or issued.',
        source: 'database/migrations/110_bl176_gate4_client_notification_triggers.sql: bl176_gate4_invoice_client_confirmation',
    },
    'client.payment.confirmed': {
        definition: 'Confirms a payment, card authorization, or held deposit for the client.',
        who: 'The payment account owner, or the linked invoice account owner.',
        when: 'A payment is created or changes to paid, pre-authorized, or deposit-held.',
        source: 'database/migrations/110_bl176_gate4_client_notification_triggers.sql: bl176_gate4_payment_client_confirmation',
    },
    'client.post_trip_reminder': {
        definition: 'Asks the client to rate a trip and optionally leave a tip.',
        who: 'The trip account owner.',
        when: 'Needs Val review: the original trip-completion trigger was removed; no current sending trigger was found.',
        needsReview: true,
        source: 'database/migrations/110_bl176_gate4_client_notification_triggers.sql: bl176_gate4_engagement_client_post_trip; database/migrations/155_bl377_website_consent_autopay_trip_alerts.sql: DROP TRIGGER',
    },
    'client.reschedule.confirmed': {
        definition: 'Confirms a change to the time of a booked trip.',
        who: 'The trip account owner.',
        when: 'A schedule that remains confirmed has its start or end time changed.',
        source: 'database/migrations/110_bl176_gate4_client_notification_triggers.sql: bl176_gate4_schedule_client_reschedule',
    },
    'client.trip.arrived': {
        definition: 'Tells the client that their driver has arrived at pickup.',
        who: 'The trip account owner.',
        when: 'An assignment is created or changes to on location, or an affiliate records a DOS arrival update.',
        source: 'database/migrations/155_bl377_website_consent_autopay_trip_alerts.sql: bl377_assignment_arrival_notification; database/migrations/110_bl176_gate4_client_notification_triggers.sql: bl176_gate4_affiliate_client_trip_status',
    },
    'client.trip.dispatched': {
        definition: 'Is intended to tell the client that dispatch has offered their trip to a driver.',
        who: 'The trip account owner.',
        when: 'Queued when an assignment is created or changes to offered. Needs Val review: the current trip-alert channel list has no entry for this event, so delivery is skipped.',
        needsReview: true,
        source: 'database/migrations/155_bl377_website_consent_autopay_trip_alerts.sql: bl176_gate4_assignment_client_trip_status; src/lib/tripNotificationPreferences.js: tripAlertChannels',
    },
    'client.trip.en_route': {
        definition: 'Is intended to tell the client that their driver is on the way to pickup.',
        who: 'The trip account owner.',
        when: 'Queued when an assignment is created or changes to en route, or an affiliate records a DER departure update. Needs Val review: the current trip-alert channel list has no entry for this event, so delivery is skipped.',
        needsReview: true,
        source: 'database/migrations/155_bl377_website_consent_autopay_trip_alerts.sql: bl176_gate4_assignment_client_trip_status; database/migrations/110_bl176_gate4_client_notification_triggers.sql: bl176_gate4_affiliate_client_trip_status; src/lib/tripNotificationPreferences.js: tripAlertChannels',
    },
    'client.trip.reservation_confirmed': {
        definition: 'Confirms a reservation and shares the pickup details.',
        who: 'The trip account owner.',
        when: 'A trip is created or changes to confirmed, once per trip.',
        source: 'database/migrations/155_bl377_website_consent_autopay_trip_alerts.sql: bl377_engagement_trip_notification',
    },
    'client.trip.thank_you': {
        definition: 'Thanks the client for their trip and shares the available invoice or receipt link.',
        who: 'The trip account owner; receipt links depend on the contact having autopay enabled.',
        when: 'A trip is created or changes to completed, once per trip.',
        source: 'database/migrations/155_bl377_website_consent_autopay_trip_alerts.sql: bl377_engagement_trip_notification; src/lib/tripNotificationPreferences.js: thankYouDeliveryPayload',
    },
    'client.trip.upcoming_24hrs': {
        definition: 'Reminds the client of tomorrow\'s pickup and available driver and vehicle details.',
        who: 'The trip account owner.',
        when: 'The scheduled check finds a confirmed booking more than 23 and no more than 24 hours away, with a confirmed or scheduled trip, once per trip.',
        source: 'database/migrations/155_bl377_website_consent_autopay_trip_alerts.sql: bl377_queue_due_upcoming_trip_notifications',
    },
    'driver.affiliate_trip_status_link': {
        definition: 'Gives an affiliate driver a no-login link to report trip progress.',
        who: 'The affiliate driver attached to the assignment, through their linked user profile.',
        when: 'Dispatch creates a new trip-status link for an affiliate assignment; opening or reusing a link does not send it again.',
        source: 'api/affiliate/trip-links.js: queueAffiliateTripLinkNotification',
    },
    'driver.application.started': {
        definition: 'Tells the hiring lead that a candidate has started their application.',
        who: 'The applicant\'s assigned hiring lead, not the candidate.',
        when: 'The application portal first marks the application started after phone verification.',
        source: 'supabase/functions/driver-application-auth/index.ts: markApplicationStarted; database/migrations/120_bl353_gate1_onboarding_steps.sql: bl353_queue_application_started_notification',
    },
    'driver.assignment.accepted': {
        definition: 'Confirms that a driver has accepted an assignment.',
        who: 'The assignment\'s driver or affiliate through their linked user profile.',
        when: 'An assignment is created or changes to accepted.',
        source: 'database/migrations/111_bl176_gate5_driver_notification_triggers.sql: bl176_gate5_assignment_driver_status',
    },
    'driver.assignment.cancelled': {
        definition: 'Confirms an active-trip cancellation and that dispatch has been notified.',
        who: 'The driver or affiliate who held the assignment before cancellation.',
        when: 'An accepted, en-route, or on-location assignment changes to cancelled with a reason.',
        source: 'database/migrations/127_bl187_gate5d_cancel_active_trip.sql: bl187_gate5d_after_assignment_cancelled',
    },
    'driver.assignment.completed': {
        definition: 'Confirms that a driver\'s assignment is complete.',
        who: 'The assignment\'s driver or affiliate through their linked user profile.',
        when: 'An assignment is created or changes to completed.',
        source: 'database/migrations/111_bl176_gate5_driver_notification_triggers.sql: bl176_gate5_assignment_driver_status',
    },
    'driver.assignment.declined': {
        definition: 'Confirms that an assignment was declined.',
        who: 'The assignment\'s driver or affiliate through their linked user profile.',
        when: 'An assignment is created or changes to declined.',
        source: 'database/migrations/111_bl176_gate5_driver_notification_triggers.sql: bl176_gate5_assignment_driver_status',
    },
    'driver.assignment.en_route': {
        definition: 'Confirms the driver\'s assignment status is on the way to pickup.',
        who: 'The assignment\'s driver or affiliate through their linked user profile.',
        when: 'An assignment is created or changes to en route.',
        source: 'database/migrations/111_bl176_gate5_driver_notification_triggers.sql: bl176_gate5_assignment_driver_status',
    },
    'driver.assignment.offer': {
        definition: 'Asks a driver to respond to a new assignment before the offer expires.',
        who: 'The offered assignment\'s driver or affiliate through their linked user profile.',
        when: 'An assignment is created or changes to offered; the deadline uses its recorded offer timeout.',
        source: 'database/migrations/111_bl176_gate5_driver_notification_triggers.sql: bl176_gate5_assignment_driver_status',
    },
    'driver.assignment.on_location': {
        definition: 'Confirms that the driver is at the pickup location.',
        who: 'The assignment\'s driver or affiliate through their linked user profile.',
        when: 'An assignment is created or changes to on location.',
        source: 'database/migrations/111_bl176_gate5_driver_notification_triggers.sql: bl176_gate5_assignment_driver_status',
    },
    'driver.assignment.passenger_onboard': {
        definition: 'Confirms that the passenger is onboard for an assignment.',
        who: 'The assignment\'s driver or affiliate through their linked user profile.',
        when: 'An assignment is created or changes to passenger onboard.',
        source: 'database/migrations/111_bl176_gate5_driver_notification_triggers.sql: bl176_gate5_assignment_driver_status',
    },
    'driver.assignment.reassigned': {
        definition: 'Tells a driver that their assignment has been marked for reassignment.',
        who: 'The driver or affiliate still attached to that assignment through their linked user profile.',
        when: 'An assignment is created or changes to reassigned.',
        source: 'database/migrations/111_bl176_gate5_driver_notification_triggers.sql: bl176_gate5_assignment_driver_status',
    },
    'driver.assignment.timed_out': {
        definition: 'Tells a driver that an assignment offer has expired.',
        who: 'The assignment\'s driver or affiliate through their linked user profile.',
        when: 'An assignment is created or changes to timed out.',
        source: 'database/migrations/111_bl176_gate5_driver_notification_triggers.sql: bl176_gate5_assignment_driver_status',
    },
    'driver.compliance.background_check_complete': {
        definition: 'Tells the hiring lead that a candidate\'s background-check result has arrived.',
        who: 'The applicant\'s assigned hiring lead, not the candidate.',
        when: 'The Certn callback reports a finished check or a cleared or flagged result.',
        source: 'api/vendors/certn/webhook.js; database/migrations/121_bl353_gate2_compliance_stage_engine.sql: bl353_queue_compliance_notification',
    },
    'driver.compliance.completed': {
        definition: 'Tells the hiring lead that all selected compliance checks have been resolved.',
        who: 'The applicant\'s assigned hiring lead, not the candidate.',
        when: 'At least one check was selected, none remain unresolved, and completion has not already been recorded; resolved can include flagged, waived, or overridden results.',
        source: 'database/migrations/121_bl353_gate2_compliance_stage_engine.sql: bl353_maybe_queue_compliance_completed',
    },
    'driver.compliance.requires_attention': {
        definition: 'Warns the hiring lead that a background-check result needs review.',
        who: 'The applicant\'s assigned hiring lead, not the candidate.',
        when: 'The Certn callback reports a flagged background-check result.',
        source: 'api/vendors/certn/webhook.js; database/migrations/121_bl353_gate2_compliance_stage_engine.sql: bl353_queue_compliance_notification',
    },
    'driver.compliance.started': {
        definition: 'Tells the hiring lead that a candidate\'s selected compliance checks have started.',
        who: 'The applicant\'s assigned hiring lead, not the candidate.',
        when: 'The compliance-start request first records a start time for the applicant.',
        source: 'api/vendors/certn/applicant-compliance.js; database/migrations/121_bl353_gate2_compliance_stage_engine.sql: bl353_queue_compliance_notification',
    },
    'driver.hire.finalized': {
        definition: 'Tells the hiring lead that a driver hire has been finalized.',
        who: 'The applicant\'s assigned hiring lead, not the candidate.',
        when: 'The applicant pipeline moves a driver candidate to active and links their onboarding record to a driver.',
        source: 'src/components/settings/ApplicantPipelineView.jsx: updateApplicant; database/migrations/122_bl353_gate3_hire_to_driver.sql: bl353_queue_hire_notification',
    },
    'driver.payout.approved': {
        definition: 'Tells an affiliate driver that a payout has been approved.',
        who: 'The payable\'s affiliate driver through their linked user profile.',
        when: 'A payable is created or changes to approved.',
        source: 'database/migrations/111_bl176_gate5_driver_notification_triggers.sql: bl176_gate5_affiliate_payout_status',
    },
    'driver.payout.clawback': {
        definition: 'Tells an affiliate driver that money is being taken back from a payout.',
        who: 'The payable\'s affiliate driver through their linked user profile.',
        when: 'A payable is created or changes to clawback.',
        source: 'database/migrations/111_bl176_gate5_driver_notification_triggers.sql: bl176_gate5_affiliate_payout_status',
    },
    'driver.payout.disbursed': {
        definition: 'Tells an affiliate driver that a payout has been sent.',
        who: 'The payable\'s affiliate driver through their linked user profile.',
        when: 'A payable is created or changes to disbursed.',
        source: 'database/migrations/111_bl176_gate5_driver_notification_triggers.sql: bl176_gate5_affiliate_payout_status',
    },
    'driver.payout.disputed': {
        definition: 'Tells an affiliate driver that a payout is under dispute.',
        who: 'The payable\'s affiliate driver through their linked user profile.',
        when: 'A payable is created or changes to disputed.',
        source: 'database/migrations/111_bl176_gate5_driver_notification_triggers.sql: bl176_gate5_affiliate_payout_status',
    },
    'driver.payout.pending_review': {
        definition: 'Tells an affiliate driver that a payout is waiting for review.',
        who: 'The payable\'s affiliate driver through their linked user profile.',
        when: 'A payable is created or changes to pending review.',
        source: 'database/migrations/111_bl176_gate5_driver_notification_triggers.sql: bl176_gate5_affiliate_payout_status',
    },
    'driver.payout.waived': {
        definition: 'Tells an affiliate driver that a payout has been waived.',
        who: 'The payable\'s affiliate driver through their linked user profile.',
        when: 'A payable is created or changes to waived.',
        source: 'database/migrations/111_bl176_gate5_driver_notification_triggers.sql: bl176_gate5_affiliate_payout_status',
    },
    'staff.balance_overdue': {
        definition: 'Flags an unpaid invoice whose due date has passed.',
        who: 'Organization owners and staff with dispatch viewer, editor, or manager access.',
        when: 'An invoice is created or relevant billing fields change while it is overdue, still open, and has money outstanding; this is not a daily timer.',
        source: 'database/migrations/115_bl176_gate9_staff_operational_alerting.sql: bl176_gate9_invoice_balance_overdue',
    },
    'staff.dead_zone_approaching': {
        definition: 'Flags upcoming work in a time window with historically quiet demand.',
        who: 'Staff with dispatch viewer, editor, or manager access who have enabled an allowed channel.',
        when: 'A proposed, held, or confirmed schedule is created or updated for the next 24 hours in a recorded dead zone.',
        source: 'database/migrations/115_bl176_gate9_staff_operational_alerting.sql: bl176_gate9_schedule_dead_zone_approaching',
    },
    'staff.dispatch_assignment_cancelled': {
        definition: 'Warns dispatch that a driver cancelled an active trip and coverage needs attention.',
        who: 'Organization owners and staff with dispatch viewer, editor, or manager access.',
        when: 'An accepted, en-route, or on-location assignment changes to cancelled with a reason.',
        source: 'database/migrations/127_bl187_gate5d_cancel_active_trip.sql: bl187_gate5d_after_assignment_cancelled',
    },
    'staff.dispatch_critical_reassignment': {
        definition: 'Warns staff that an assignment has been marked for reassignment.',
        who: 'Organization owners, staff with dispatch viewer, editor, or manager access, and the attached driver or affiliate.',
        when: 'An assignment is created or changes to reassigned; the trigger does not require a previous accepted status.',
        source: 'database/migrations/115_bl176_gate9_staff_operational_alerting.sql: bl176_gate9_dispatch_critical_reassignment',
    },
    'staff.goal_gap': {
        definition: 'Flags an active dashboard goal that is still short of its target.',
        who: 'Organization owners and staff with dispatch viewer, editor, or manager access.',
        when: 'A goal is created or its target or active setting changes, and the dashboard shows a positive remaining gap.',
        source: 'database/migrations/115_bl176_gate9_staff_operational_alerting.sql: bl176_gate9_dashboard_goal_gap',
    },
    'staff.inbound_message': {
        definition: 'Alerts staff to a new inbound text with a preview and a link to its conversation.',
        who: 'Organization owners and staff with dispatch viewer, editor, or manager access.',
        when: 'An inbound SMS or MMS is appended to a conversation, subject to the organization\'s per-thread throttle, quiet hours, and allowed channels; outbound messages do not trigger it.',
        source: 'database/migrations/277_retire_legacy_notifications.sql: bl177_inbound_message_staff_alert',
    },
    'staff.kpi_trend': {
        definition: 'Flags revenue change or goal shortfall that meets a configured alert threshold.',
        who: 'Organization owners and staff with dispatch viewer, editor, or manager access who have enabled an allowed channel.',
        when: 'An active revenue-change or goal-gap threshold is created or updated and its comparison matches the current dashboard value; this is not continuous monitoring.',
        source: 'database/migrations/115_bl176_gate9_staff_operational_alerting.sql: bl176_gate9_dashboard_kpi_trend',
    },
    'staff.vip_flag': {
        definition: 'Flags an account that has just entered a VIP customer tier.',
        who: 'Organization owners and staff with dispatch viewer, editor, or manager access.',
        when: 'An account is created in a VIP tier or changes from a non-VIP tier to a VIP tier.',
        source: 'database/migrations/115_bl176_gate9_staff_operational_alerting.sql: bl176_gate9_account_vip_flag',
    },
};

export const NOTIFICATION_HELP_CONTENT = {
    ...LIVE_EXPORT_NOTIFICATION_HELP_CONTENT,
    ...TRIP_FEEDBACK_NOTIFICATION_HELP,
    ...EVENT_REQUEST_NOTIFICATION_HELP,
    ...NOTE_MENTION_NOTIFICATION_HELP,
};
